import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
// Note: useRef retained for cacheRef / cacheTimestampRef / cachedUserIdRef below.
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PERFIL_MAP } from '@/lib/helpers'

export interface PerfilPermissao {
  perfil: string
  modulo: string
  pode_ver: boolean
  pode_criar: boolean
  pode_editar: boolean
  pode_excluir: boolean
}

export interface PermissaoModulo {
  pode_ver: boolean
  pode_criar: boolean
  pode_editar: boolean
  pode_excluir: boolean
}

const EMPTY_PERM: PermissaoModulo = { pode_ver: false, pode_criar: false, pode_editar: false, pode_excluir: false }

function normalizePerfil(perfil: string): string {
  return PERFIL_MAP[perfil] || perfil
}

const CACHE_TTL = 5 * 60 * 1000 // 5 min

export function usePerfilPermissions() {
  const { user } = useAuth()
  const [allPermissions, setAllPermissions] = useState<Map<string, Map<string, PermissaoModulo>>>(new Map())
  const [loading, setLoading] = useState(true)

  const cacheRef = useRef<Map<string, Map<string, PermissaoModulo>>>(new Map())
  const cacheTimestampRef = useRef(0)
  const cachedUserIdRef = useRef<string | null>(null)

  // Reset cache when user changes or logs out
  useEffect(() => {
    const currentId = user?.id ?? null
    if (cachedUserIdRef.current !== currentId) {
      cacheRef.current = new Map()
      cacheTimestampRef.current = 0
      cachedUserIdRef.current = currentId
      if (!currentId) {
        setAllPermissions(new Map())
        setLoading(false)
      }
    }
  }, [user?.id])

  const loadPermissions = useCallback(async () => {
    if (!user?.id) return

    const now = Date.now()
    if (cacheRef.current.size > 0 && now - cacheTimestampRef.current < CACHE_TTL) {
      setAllPermissions(cacheRef.current)
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase
        .from('perfil_permissoes')
        .select('perfil, modulo, pode_ver, pode_criar, pode_editar, pode_excluir')
        .eq('company_id', user.company_id)

      if (error) {
        console.error('[Permissões] Erro ao carregar:', error)
        setLoading(false)
        return
      }

      const map = new Map<string, Map<string, PermissaoModulo>>()
      for (const row of data || []) {
        if (!map.has(row.perfil)) map.set(row.perfil, new Map())
        map.get(row.perfil)!.set(row.modulo, {
          pode_ver: row.pode_ver,
          pode_criar: row.pode_criar,
          pode_editar: row.pode_editar,
          pode_excluir: row.pode_excluir,
        })
      }

      cacheRef.current = map
      cacheTimestampRef.current = now
      setAllPermissions(map)
    } catch (err) {
      console.error('[Permissões] Erro inesperado:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (user?.id) loadPermissions()
  }, [loadPermissions, user?.id])

  // NOTE: Realtime subscription was removed (P2-07 revert).
  // usePerfilPermissions is called in multiple components (AppSidebar, Estoque, etc.).
  // Each instance tried to create a channel with the same name — the second call
  // received an already-subscribed channel from Supabase and crashed on .on().
  // The 5-minute TTL cache + invalidateCache() are sufficient for permission changes,
  // which are rare admin operations. A Realtime subscription would require a
  // singleton provider to be safe; deferred to a future iteration.

  const userPerfil = useMemo(() => normalizePerfil(user?.perfil || 'visualizador'), [user?.perfil])

  const userPermissions = useMemo(() => {
    return allPermissions.get(userPerfil) || new Map()
  }, [allPermissions, userPerfil])

  const getModuloPerm = useCallback((modulo: string): PermissaoModulo => {
    return userPermissions.get(modulo) || EMPTY_PERM
  }, [userPermissions])

  const podeVer = useCallback((modulo: string): boolean => {
    return getModuloPerm(modulo).pode_ver
  }, [getModuloPerm])

  const podeCriar = useCallback((modulo: string): boolean => {
    return getModuloPerm(modulo).pode_criar
  }, [getModuloPerm])

  const podeEditar = useCallback((modulo: string): boolean => {
    return getModuloPerm(modulo).pode_editar
  }, [getModuloPerm])

  const podeExcluir = useCallback((modulo: string): boolean => {
    return getModuloPerm(modulo).pode_excluir
  }, [getModuloPerm])

  const getPerfilPermissions = useCallback((perfil: string): Map<string, PermissaoModulo> => {
    return allPermissions.get(perfil) || new Map()
  }, [allPermissions])

  const perfisDisponiveis = useMemo(() => {
    return Array.from(allPermissions.keys()).sort()
  }, [allPermissions])

  const invalidateCache = useCallback(() => {
    cacheRef.current = new Map()
    cacheTimestampRef.current = 0
    loadPermissions()
  }, [loadPermissions])

  return {
    loading,
    userPerfil,
    userPermissions,
    getModuloPerm,
    podeVer,
    podeCriar,
    podeEditar,
    podeExcluir,
    getPerfilPermissions,
    perfisDisponiveis,
    allPermissions,
    invalidateCache,
    normalizePerfil,
  }
}
